import React from "react";
import { useIntl } from "react-intl";

import BreadCrumb from "@components/Breadcrumb";
import { ButtonLinkIcon } from "@components/Button";
import Card from "@components/Card";
import Div from "@components/Div";
import FullSizeDialog from "@components/FullSizeDialog";
import { H4 } from "@components/Heading";
import Icon from "@components/Icon";
import Link from "@components/Link";
import ProgressSpinner from "@components/ProgressSpinner";
import { Text, TextMediumWeight } from "@components/Text";

import AdminContainer from "@layout/AdminContainer";

import { ROUTES } from "@utils/constant";

import { CouponForm as UpdateCouponForm } from "../components";

import { BREADCRUMB_ITEMS } from "./UpdateCoupon.constants";
import { useUpdateCoupon } from "./UpdateCoupon.hooks";
import ValidationSchema from "./UpdateCoupon.schema";

const UpdateCoupon = () => {
  const { messages } = useIntl();

  const {
    isEditPermission,
    config,
    isUpdateDialogVisible,
    isLoading,
    handleUpdateCoupon,
    handleShowUpdateForm,
    handleCloseUpdateForm,
    headerConfig,
    createdAtData,
    createdByData,
    couponDetails,
  } = useUpdateCoupon();

  return (
    <AdminContainer config={headerConfig}>
      <Div width={1}>
        <BreadCrumb p={0} mb={4} items={BREADCRUMB_ITEMS} />

        {isLoading && <ProgressSpinner />}

        <Card my={3} p={3} borderRadius="20px">
          <Div
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <H4>{messages.title_coupon_configuration}</H4>

            {isEditPermission && (
              <ButtonLinkIcon
                label={
                  <TextMediumWeight>{messages.label_edit}</TextMediumWeight>
                }
                onClick={handleShowUpdateForm}
                icon={
                  <Icon
                    name="pen"
                    mr={2}
                    color="var(--turquoise)"
                    fontSize="var(--fs-icon-s)"
                  />
                }
              />
            )}
          </Div>

          <Div
            mt={2}
            width={1}
            height="1px"
            backgroundColor="var(--grey-lightest)"
          />

          <Div
            mt={3}
            display="flex"
            flexDirection={["column", "column", "row", "row"]}
            flexWrap={["nowrap", "nowrap", "wrap", "wrap"]}
            gridGap={3}
          >
            {config.couponConfiguration.map(({ label, value }) => (
              <Div flex="1 1 40%" key={label}>
                <TextMediumWeight>
                  {`${label}: `}
                  <Text>{value}</Text>
                </TextMediumWeight>
              </Div>
            ))}
          </Div>
        </Card>

        <Card my={3} p={3} borderRadius="20px">
          <H4>{messages.title_registration}</H4>

          <Div
            mt={2}
            width={1}
            height="1px"
            backgroundColor="var(--grey-lightest)"
          />

          <Div
            mt={3}
            display="flex"
            flexDirection={["column", "column", "row", "row"]}
            flexWrap={["nowrap", "nowrap", "wrap", "wrap"]}
            gridGap={3}
          >
            <Div flex="1 1 40%" key={createdAtData.label}>
              <TextMediumWeight>
                {`${createdAtData.label}: `}
                <Text>{createdAtData.value}</Text>
              </TextMediumWeight>
            </Div>

            <Div flex="1 1 40%" key={createdByData.label}>
              <TextMediumWeight display="flex">
                {`${createdByData.label}: `}
                <Link
                  ml="2px"
                  href={ROUTES.ADMIN_ADMINS_LIST.URL}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {createdByData.value?.name}
                </Link>
              </TextMediumWeight>
            </Div>
          </Div>
        </Card>

        {isUpdateDialogVisible && (
          <FullSizeDialog
            title={messages.heading_basic_information}
            onClose={handleCloseUpdateForm}
          >
            <UpdateCouponForm
              isUpdateForm
              initialData={couponDetails}
              validationSchema={ValidationSchema}
              onSubmit={handleUpdateCoupon}
              onCancel={handleCloseUpdateForm}
            />
          </FullSizeDialog>
        )}
      </Div>
    </AdminContainer>
  );
};

export default UpdateCoupon;
